import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import * as Sentry from '@sentry/nextjs';
import AWS from 'aws-sdk';

import type { LandingPage, LandingPageLanguage } from '../../../lib/strapi';
import { i18n } from '../../../config/i18n.config';

import { ContactFieldConfig } from './ContactDetailsForm.config';
import { isValidContactDetailsData } from './ContactDetailsForm.validator';

import { Button } from '../../Button';
import { Field } from '../Field';

// S3 configuration
const s3 = new AWS.S3({
  region: 'eu-central-1',
  accessKeyId: process.env.NEXT_PUBLIC_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.NEXT_PUBLIC_AWS_SECRET_ACCESS_KEY,
});

export type ContactDetailsFormValues = {
  [ContactFieldConfig.Salutation.id]?: string | undefined;
  [ContactFieldConfig.FirstName.id]?: string | undefined;
  [ContactFieldConfig.LastName.id]?: string | undefined;
  [ContactFieldConfig.Email.id]?: string | undefined;
  [ContactFieldConfig.Phone.id]?: string | undefined;
  [ContactFieldConfig.TermsAccepted.id]?: boolean | undefined;
  [ContactFieldConfig.s3UploadUrls.id]?:
    | { name: string; url: string }[]
    | undefined;
};

export type ContactDetailsFormProps = {
  values?: ContactDetailsFormValues;
  setValues: (values: ContactDetailsFormValues) => void;
  onSubmit?: (cb?: () => void) => Promise<void>;
  className?: string;
  buttonText?: string | null;
  language: LandingPageLanguage;
  content?: LandingPage;
  buttonCaption?: string | null;
};

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  values,
  onSubmit,
  setValues,
  className,
  buttonText,
  language,
  buttonCaption,
  content,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accumulatedFiles, setAccumulatedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]); // Track names or identifiers of uploaded files
  const [isSubmissionFailed, setIsSubmissionFailed] = useState(false);
  const [isInvalidInput, setIsInvalidInput] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [filesInfo, setFilesInfo] = useState<
    { name: string; progress: number }[]
  >([]);

  const SalutationField = ContactFieldConfig.Salutation.getConfig(language);
  const FirstNameField = ContactFieldConfig.FirstName.getConfig(language);
  const LastNameField = ContactFieldConfig.LastName.getConfig(language);
  const EmailField = ContactFieldConfig.Email.getConfig(language);
  const PhoneField = ContactFieldConfig.Phone.getConfig(language);
  const TermsAcceptedField =
    ContactFieldConfig.TermsAccepted.getConfig(language);

  const handleSubmit = useCallback(async () => {
    if (!isValidContactDetailsData(values)) {
      setIsInvalidInput(true);
      return;
    }

    setIsInvalidInput(false);
    setIsSubmissionFailed(false);
    setIsSubmitting(true);

    try {
      await onSubmit?.(() => {
        setIsSubmitting(false);
      });
    } catch (err) {
      setIsSubmissionFailed(true);
      setIsSubmitting(false);
      Sentry.captureException(err);
    }
  }, [onSubmit, values]);

  const uploadFileToS3 = async (
    file: File,
    updateProgress: (progress: number) => void,
  ): Promise<string> => {
    const params = {
      // Bucket params
      Bucket: process.env.NEXT_PUBLIC_CONTACT_FORM_UPLOADS_S3_BUCKET_NAME!,
      Key: `${file.name}`,
      Body: file,
    };

    return new Promise((resolve, reject) => {
      s3.upload(params)
        .on('httpUploadProgress', function (event) {
          const progress = Math.round((event.loaded / event.total) * 100);
          updateProgress(progress);
        })
        .send(function (err: any, data: any) {
          if (err) {
            reject(err);
          }
          resolve(data.Location);
        });
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files) {
      setIsUploading(true);
      const newFilesArray = Array.from(e.target.files);

      // Filter out files that have already been uploaded
      const filesToUpload = newFilesArray.filter(
        (file) => !uploadedFiles.includes(file.name),
      );

      if (filesToUpload.length === 0) {
        setIsUploading(false);
        return; // No new files to upload
      }

      // Combine new files with previously selected files
      const combinedFiles = [...accumulatedFiles, ...filesToUpload];
      setAccumulatedFiles(combinedFiles);

      const initialFilesInfo = combinedFiles.map((file) => ({
        name: file.name,
        progress: uploadedFiles.includes(file.name) ? 100 : 0,
        url: '',
      }));
      setFilesInfo(initialFilesInfo);

      const uploadPromises = filesToUpload.map((file) => {
        return uploadFileToS3(file, (progress) => {
          setFilesInfo((prevState) => {
            // Find the index of the current file in the prevState
            const fileIndex = prevState.findIndex((f) => f.name === file.name);
            if (fileIndex === -1) return prevState; // In case file is not found, return prevState

            // Update the progress of the specific file
            const newState = [...prevState];
            newState[fileIndex].progress = progress;
            return newState;
          });
        }).then((uploadedUrl) => {
          setUploadedFiles((prev) => [...prev, file.name]);
          return uploadedUrl;
        });
      });

      const s3Urls = await Promise.all(uploadPromises);

      setFilesInfo((prevState) =>
        prevState.map((file, index) => ({
          ...file,
          url: s3Urls[index],
        })),
      );

      const updatedUrlsArray = filesToUpload.map((file, index) => ({
        name: file.name,
        url: s3Urls[index],
      }));

      // Now set the combined URLs
      setValues({ ...values, s3UploadUrls: Object.values(updatedUrlsArray) });
      setIsUploading(false); // End the upload process
    }
  };

  useEffect(() => {
    setIsInvalidInput(false);
  }, [values]);

  return (
    <>
      <form
        className={cx('flex h-auto w-full flex-col justify-start', className)}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div className="grid grid-flow-row grid-cols-1 gap-6 md:grid-flow-row md:grid-cols-2">
          <Field
            type="radio"
            className="md:col-span-2 md:!max-w-full"
            id={ContactFieldConfig.Salutation.id}
            options={SalutationField.label}
            value={values?.salutation}
            error={isInvalidInput && !values?.salutation}
            onChange={(v) => {
              console.log(SalutationField.label);
              setValues({ ...values, salutation: v });
            }}
          />
          <Field
            type="text"
            id={ContactFieldConfig.FirstName.id}
            label={FirstNameField.label}
            validators={FirstNameField.validators}
            error={isInvalidInput && !values?.firstName}
            value={values?.firstName}
            onChange={(e) => {
              setValues({ ...values, firstName: e.target.value });
            }}
          />
          <Field
            type="text"
            id={ContactFieldConfig.LastName.id}
            label={LastNameField.label}
            validators={LastNameField.validators}
            error={isInvalidInput && !values?.lastName}
            value={values?.lastName}
            onChange={(e) => {
              setValues({ ...values, lastName: e.target.value });
            }}
          />
          <Field
            type="text"
            id={ContactFieldConfig.Phone.id}
            label={PhoneField.label}
            validators={PhoneField.validators}
            error={isInvalidInput && !values?.phone}
            value={values?.phone}
            onChange={(e) => {
              setValues({ ...values, phone: e.target.value });
            }}
          />
          <Field
            type="email"
            id={ContactFieldConfig.Email.id}
            label={EmailField.label}
            validators={EmailField.validators}
            error={isInvalidInput && !values?.email}
            value={values?.email}
            onChange={(e) => {
              setValues({ ...values, email: e.target.value });
            }}
          />
          <Field
            type="checkbox"
            className="md:col-span-2 md:!max-w-full"
            id={ContactFieldConfig.TermsAccepted.id}
            value={values?.acceptedTerms}
            label={TermsAcceptedField.label}
            error={isInvalidInput && !values?.acceptedTerms}
            onChange={() => {
              setValues({ ...values, acceptedTerms: !values?.acceptedTerms });
            }}
          />
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative my-4 flex w-full justify-center md:my-6">
            {isSubmissionFailed || isInvalidInput ? (
              <span className="absolute -top-2 left-0 block w-full px-4 text-center text-sm tracking-tight text-[indianred]">
                {getErrorMessage(language, {
                  isSubmissionFailed,
                  isInvalidInput,
                })}
              </span>
            ) : undefined}
            <Button
              variant="primary"
              label={buttonText ?? i18n[language].FORM_SUBMIT_CTA}
              className="my-4"
              data-testid="contact-details-form-submit"
              loading={isSubmitting}
              disabled={isSubmitting || isUploading}
              onClick={handleSubmit}
            />
            <span className="absolute -bottom-3 left-0 w-full text-center text-xs sm:text-[0.8rem]">
              {buttonCaption}
            </span>
          </div>
          {content?.fileuploadquestionnaire && (
            <label className="upload-button flex-col items-end">
              <Button
                variant="secondary"
                label={i18n[language].UPLOAD_FILE}
                className="w-60"
                onClick={(e: any) => {
                  e.preventDefault(); // Prevent form submission when the button is clicked
                  document.getElementById('file')?.click(); // Programmatically trigger the file input
                }}
              />
              <Field
                type="file"
                id="file"
                label="Upload Files"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}
        </div>
      </form>
      {filesInfo.map((fileInfo, index) => {
        // Define the maximum number of characters before the truncation
        const maxDisplayLength = 18; // You can adjust this number as needed

        // Extract the file extension
        const fileExtension = fileInfo.name.slice(
          fileInfo.name.lastIndexOf('.'),
        );

        // Determine if the filename needs to be truncated
        const needsTruncation =
          fileInfo.name.length > maxDisplayLength + fileExtension.length;

        // Construct the display name with truncation if necessary
        const displayName = needsTruncation
          ? `${fileInfo.name.substring(0, maxDisplayLength)}...${fileExtension}`
          : fileInfo.name;

        return (
          <div key={index} className="flex justify-end">
            <span>{displayName}</span>
            <progress
              className="custom-color mt-1"
              value={fileInfo.progress}
              max="100"
            ></progress>
          </div>
        );
      })}
    </>
  );
};

const getErrorMessage = (
  language: LandingPageLanguage,
  { isSubmissionFailed, isInvalidInput }: Record<string, boolean>,
) => {
  if (isInvalidInput) {
    return i18n[language].FORM_INVALID_FIELDS;
  }
  if (isSubmissionFailed) {
    return i18n[language].FORM_SUBMIT_ERROR;
  }
  return '';
};
